import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getVarsoUrl } from "../../../../common/helpers/helpers";
import Expander from "../../../../components/Expander";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import showCanonicalLegend from "../../../../common/helpers/showCanonicalLegend";
import {
  includeManePlus,
  includeManeSelect,
  includeAppris,
  includeUniprotId,
} from "../../../variant/components/transcripts/helpers";
import {
  getVarsomeHost,
  getGenomeName,
} from "../../../../state/selectors/general.selectors";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import { GeneTranscriptType } from "../../../../components/componentTypes/GeneTranscriptsType";
import ConditionalLink from "../../../../components/atoms/ConditionalLink/ConditonalLink";
import HelperText from "../../../../components/atoms/HelperText";

const GeneTranscripts = ({ data, hostUrl, genomeName }) => {
  const [transcripts, setTranscripts] = useState([]);

  const compare = (a, b) => {
    return a - b;
  };

  useEffect(() => {
    if (data && data?.length > 0) {
      // Sort by canonical, then by name.
      const sortedTranscripts = data.sort((a, b) => {
        if (a.canonical && b.canonical && !a.name.startsWith("ENST")) return -1;
        if (a.canonical && b.canonical && a.name.startsWith("ENST")) return 1;
        if (a.canonical) return -1;
        if (b.canonical) return 1;
        if (!a.name.startsWith("ENST") && b.name.startsWith("ENST")) {
          return -1;
        }
        if (a.name.startsWith("ENST") && !b.name.startsWith("ENST")) {
          return 1;
        }
        if (a.name.startsWith("ENST") && b.name.startsWith("ENST")) {
          const aName = a.name.substr(4);
          const bName = b.name.substr(4);
          return compare(aName, bName);
        }
        if (!a.name.startsWith("ENST") && !b.name.startsWith("ENST")) {
          const aName = a.name.split("_");
          const bName = b.name.split("_");
          if (aName[0] === bName[0]) return compare(aName[1], bName[1]);
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        }
        return -1;
      });
      setTranscripts(sortedTranscripts);
    }
  }, [data]);

  if (transcripts && transcripts?.length > 0) {
    const genomeSubdomain = genomeName === "hg38" ? "www" : "grch37";

    const content = (
      <React.Fragment>
        <div className="varsome-inner-container">
          <table className="varsome-table">
            <thead>
              <tr>
                <th width="10%">Name</th>
                <th width="7%">Strand</th>
                <th width="14%">Chromosome</th>
                <th width="7%">Position</th>
                <th width="7%">Length</th>
                <th width="9%">mRNA length</th>
                <th width="9%">Coding length</th>
                <th width="5%">
                  TSL
                  <HelperText fieldDescriptionKey="TSL" />
                </th>
                {includeManePlus(transcripts) && (
                  <th width="8%">
                    {"MANE Plus Clinical"}{" "}
                    <HelperText fieldDescriptionKey="MANE Plus Clinical" />
                  </th>
                )}
                {includeManeSelect(transcripts) && (
                  <th width="8%">
                    {"MANE Select "}
                    <HelperText fieldDescriptionKey="MANE Select" />
                  </th>
                )}
                {includeAppris(transcripts) && (
                  <th width="8%">
                    {"APPRIS "} <HelperText fieldDescriptionKey="APPRIS" />
                  </th>
                )}
                {includeUniprotId(transcripts) && (
                  <th width="8%">
                    UniProt Accession{" "}
                    <HelperText fieldDescriptionKey="UniProt Accession" />
                  </th>
                )}
              </tr>
            </thead>
            <Expander showMore="10" component="tbody">
              {transcripts.map((item, i) => {
                return (
                  <tr
                    key={i}
                    className={item.canonical ? "varsome-canonical-row" : ""}
                  >
                    <td>
                      <ConditionalLink
                        href={`${hostUrl}/transcript/${genomeName}/${item.name}`}
                        text={item.name}
                        color="red"
                      />
                      <br />
                      {item.name.startsWith("ENST") ? (
                        <a
                          href={getVarsoUrl(
                            `http://${genomeSubdomain}.ensembl.org/Homo_sapiens/Transcript/Summary?t=${item.name}`
                          )}
                          target="_blank"
                        >
                          <small style={{ color: "#2ca591" }}>
                            See on Ensembl
                          </small>
                        </a>
                      ) : (
                        <a
                          href={getVarsoUrl(
                            `https://www.ncbi.nlm.nih.gov/nuccore/${item.name}`
                          )}
                          target="_blank"
                        >
                          <small style={{ color: "#2ca591" }}>
                            See on NCBI
                          </small>
                        </a>
                      )}
                    </td>
                    <td style={{ fontSize: "1.1rem" }}>
                      {item.strand === "+" ? (
                        <i className="varsome-icons icon-arrow-right2" />
                      ) : (
                        <i className="varsome-icons icon-arrow-left2" />
                      )}
                    </td>
                    <td>{item.cytoband ? item.cytoband : item.chromo}</td>
                    <td>{item.position}</td>
                    <td>{item.length}</td>
                    <td>{item.mrna_length}</td>
                    <td>
                      {item.coding_length ? item.coding_length : "Non-coding"}
                    </td>
                    <td>{item.ensembl_support_level || "-"}</td>
                    {includeManePlus(transcripts) && (
                      <td>{item?.mane_plus || "-"}</td>
                    )}
                    {includeManeSelect(transcripts) && (
                      <td>{item?.mane_select || "-"}</td>
                    )}
                    {includeAppris(transcripts) && (
                      <td>{item?.ensembl_appris || "-"}</td>
                    )}
                    {includeUniprotId(transcripts) && (
                      <td>
                        {item?.uniprot_id ? (
                          <a
                            href={`https://www.uniprot.org/uniprotkb/${item.uniprot_id}/entry`}
                            target="_blank"
                          >
                            {item.uniprot_id}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </Expander>
          </table>
          {showCanonicalLegend(transcripts)}
        </div>
      </React.Fragment>
    );

    return (
      <div
        className="varsome-result-fixed"
        data-testid="gene-transcripts-component"
      >
        <div className="varsome-container varsome-container__basic">
          <ComponentPanel title="Transcripts" content={content} />
        </div>
      </div>
    );
  }

  return null;
};

GeneTranscripts.propTypes = {
  data: GeneTranscriptType,
  hostUrl: PropTypes.string,
  genomeName: PropTypes.string,
};

GeneTranscripts.defaultProps = {
  data: null,
  hostUrl: null,
  genomeName: null,
};

const mapStateToProps = (state) => ({
  hostUrl: getVarsomeHost(state),
  genomeName: getGenomeName(state),
});

export default connect(mapStateToProps)(
  withErrorGuard(GeneTranscripts, "GeneTranscripts", "Transcripts")
);
